import { Component, Inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { ReportGenerateData } from '../../_models/reports/report-generate-data.model';

/** How many recipient emails the confirmation names before it counts the rest. */
export const NAMED_RECIPIENTS = 5;

/** What the Send confirmation shows: the report, who it goes to, and whether it has gone before. */
export interface SendReportSummary {
  reportName: string;
  from: Date;
  to: Date;
  format: 'Excel' | 'PDF';
  reportCount: number;
  /** Each client and how many reports it gets, in name order. */
  clients: { name: string; count: number }[];
  /** The distinct contact emails, in order. */
  recipients: string[];
  /** Reports for learners with no contact email: made, but nobody is emailed. */
  noRecipientCount: number;
  /** When this selection was last sent, or null. */
  lastSent: Date | null;
  isResend: boolean;
}

/** Builds the confirmation's summary from the lines the send would go out for. */
export function buildSendSummary(lines: ReportGenerateData[], reportName: string, from: Date, to: Date,
  pdf: boolean, lastSent: Date | null, isResend: boolean): SendReportSummary {
  const byClient = new Map<string, number>();
  const recipients: string[] = [];
  let noRecipientCount = 0;
  for (const line of lines) {
    const client = line.clientName || 'Unknown client';
    byClient.set(client, (byClient.get(client) ?? 0) + 1);
    const email = (line.referrerEmail ?? '').trim();
    if (!email) {
      noRecipientCount++;
    } else if (!recipients.some(r => r.toLowerCase() === email.toLowerCase())) {
      recipients.push(email);
    }
  }
  return {
    reportName, from, to,
    format: pdf ? 'PDF' : 'Excel',
    reportCount: lines.length,
    clients: [...byClient.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name)),
    recipients,
    noRecipientCount,
    lastSent,
    isResend
  };
}

/**
 * The "are you sure" before a client report goes out: what is sent, to which clients and contacts, and a warning
 * when the selection has been sent before. Cancel has the focus, so Enter never sends.
 */
@Component({
  selector: 'app-send-report-confirm',
  standalone: true,
  imports: [MatDialogModule, DatePipe],
  templateUrl: './send-report-confirm.component.html',
  styleUrls: ['./send-report-confirm.component.css']
})
export class SendReportConfirmComponent {
  public readonly namedRecipients = NAMED_RECIPIENTS;

  constructor(@Inject(MAT_DIALOG_DATA) public summary: SendReportSummary) { }

  public get title(): string {
    return this.summary.isResend ? 'Resend this report?' : 'Send this report?';
  }

  public get confirmLabel(): string {
    const n = this.summary.reportCount;
    return `${this.summary.isResend ? 'Resend' : 'Send'} ${n} report${n === 1 ? '' : 's'}`;
  }

  public get shownRecipients(): string[] {
    return this.summary.recipients.slice(0, NAMED_RECIPIENTS);
  }

  public get moreRecipients(): number {
    return Math.max(0, this.summary.recipients.length - NAMED_RECIPIENTS);
  }
}
